import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, cn } from '@vd/ui';
import { Calendar, ChevronDown } from 'lucide-react';
import styles from './Admin.module.css';

/**
 * Date-range picker shared by admin pages that scope to a window
 * (Usage today; later Analytics, Cancellations, etc.).
 *
 * Two surfaces:
 *   - Trigger: a small button showing the current range as a label
 *     (e.g. "Last 30 days" or "Mar 1, 2026 → Mar 31, 2026"). Designed
 *     to sit in a page header next to the H1.
 *   - Modal: preset list on the left, custom range fields on the
 *     right. Picking a preset commits + closes; the custom range
 *     commits on Apply.
 *
 * The "all time" preset commits a range from epoch-zero to today;
 * the backend can treat either bound liberally — passing a very-
 * old `from` is identical to "no filter" once the data window is
 * small enough.
 *
 * Wire format: `from` + `to` as YYYY-MM-DD strings (inclusive on
 * both ends). Plus an optional `preset` key the caller can use to
 * render the label without recomputing.
 */

export type PresetKey =
  | 'today'
  | 'last7'
  | 'last30'
  | 'last60'
  | 'last90'
  | 'mtd'
  | 'ytd'
  | 'last-year'
  | 'all-time'
  | 'custom';

export interface PeriodValue {
  from: string;   // YYYY-MM-DD
  to: string;     // YYYY-MM-DD
  preset: PresetKey;
}

interface Preset {
  key: PresetKey;
  label: string;
  /** Compute the range for "today". Custom doesn't have one. */
  compute?: (today: Date) => { from: string; to: string };
}

const PRESETS: Preset[] = [
  { key: 'today',     label: 'Today',         compute: (t) => ({ from: iso(t), to: iso(t) }) },
  { key: 'last7',     label: 'Last 7 days',   compute: (t) => ({ from: iso(addDays(t, -6)), to: iso(t) }) },
  { key: 'last30',    label: 'Last 30 days',  compute: (t) => ({ from: iso(addDays(t, -29)), to: iso(t) }) },
  { key: 'last60',    label: 'Last 60 days',  compute: (t) => ({ from: iso(addDays(t, -59)), to: iso(t) }) },
  { key: 'last90',    label: 'Last 90 days',  compute: (t) => ({ from: iso(addDays(t, -89)), to: iso(t) }) },
  { key: 'mtd',       label: 'Month to date', compute: (t) => ({ from: iso(startOfMonth(t)), to: iso(t) }) },
  { key: 'ytd',       label: 'Year to date',  compute: (t) => ({ from: iso(startOfYear(t)), to: iso(t) }) },
  { key: 'last-year', label: 'Last year',     compute: (t) => {
    const y = t.getUTCFullYear() - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  } },
  { key: 'all-time',  label: 'All time',      compute: () => ({ from: '1970-01-01', to: iso(new Date()) }) },
  { key: 'custom',    label: 'Custom range' },
];

/** Default preset to use as the initial value for any consumer. */
export function defaultPeriod(): PeriodValue {
  return applyPreset('last30')!;
}

/** Compute the range for a preset given today. */
export function applyPreset(key: PresetKey): PeriodValue | null {
  const p = PRESETS.find((x) => x.key === key);
  if (!p?.compute) return null;
  const r = p.compute(new Date());
  return { ...r, preset: key };
}

/** Human-readable label for a value. Preset → preset name; custom →
 *  "Mar 1 → Mar 31, 2026" style. */
export function labelFor(v: PeriodValue): string {
  if (v.preset !== 'custom') {
    return PRESETS.find((p) => p.key === v.preset)?.label ?? `${v.from} → ${v.to}`;
  }
  return `${formatShort(v.from)} → ${formatShort(v.to)}`;
}

export interface PeriodPickerProps {
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
  /** Optional className on the trigger button. */
  className?: string;
}

export function PeriodPicker({ value, onChange, className }: PeriodPickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={cn(styles.periodTrigger, className)}
        onClick={() => setOpen(true)}
        title="Change time range"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Calendar size={13} />
        <span>{labelFor(value)}</span>
        <ChevronDown size={13} className={styles.muted} />
      </button>
      <PeriodPickerModal
        open={open}
        value={value}
        onClose={() => setOpen(false)}
        onApply={(next) => { onChange(next); setOpen(false); }}
      />
    </>
  );
}

interface ModalProps {
  open: boolean;
  value: PeriodValue;
  onClose: () => void;
  onApply: (next: PeriodValue) => void;
}

function PeriodPickerModal({ open, value, onClose, onApply }: ModalProps) {
  // Local state so half-typed custom dates don't commit until Apply.
  // Seed from the current value so reopening the modal preserves the
  // last selection. When the user picks a preset, immediately apply
  // + close — fewer clicks for the common case.
  const [custom, setCustom] = useState<{ from: string; to: string }>(() => ({ from: value.from, to: value.to }));
  const today = iso(new Date());

  function pickPreset(p: Preset) {
    if (p.key === 'custom') return; // custom is committed via Apply
    const r = p.compute!(new Date());
    onApply({ from: r.from, to: r.to, preset: p.key });
  }

  function applyCustom() {
    if (!validDate(custom.from) || !validDate(custom.to)) return;
    const [from, to] = custom.from <= custom.to
      ? [custom.from, custom.to]
      : [custom.to, custom.from];
    onApply({ from, to, preset: 'custom' });
  }

  return (
    <Modal open={open} onClose={onClose} width={520} labelledBy="period-picker-title">
      <ModalHeader id="period-picker-title">Time range</ModalHeader>
      <ModalBody>
        <div className={styles.periodPickerGrid}>
          <div className={styles.periodPresetList}>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={cn(styles.periodPresetItem, value.preset === p.key && styles.periodPresetActive)}
                onClick={() => pickPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={styles.periodCustomCol}>
            <div className={styles.periodCustomTitle}>Custom range</div>
            <label className={styles.periodCustomField}>
              <span>From</span>
              <input
                type="date"
                value={custom.from}
                max={today}
                onChange={(e) => setCustom((p) => ({ ...p, from: e.target.value }))}
              />
            </label>
            <label className={styles.periodCustomField}>
              <span>To</span>
              <input
                type="date"
                value={custom.to}
                max={today}
                onChange={(e) => setCustom((p) => ({ ...p, to: e.target.value }))}
              />
            </label>
            <p className={styles.muted} style={{ fontSize: 11, marginTop: 4 }}>
              Inclusive on both ends. Inverted ranges are swapped on Apply.
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={applyCustom}
          disabled={!validDate(custom.from) || !validDate(custom.to)}
        >
          Apply custom
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ---- helpers ----
function iso(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}
function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function startOfYear(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
}
function validDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}
function formatShort(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }); // a day, not a moment (TOADO-904)
  } catch {
    return iso;
  }
}
